'use client'

import { LineArrowLeft } from '@qr-front/icons/line-arrow-left'
import { SolidCheck } from '@qr-front/icons/solid-check'
import { Button } from '@qr-front/ui/components/Button'
import { Spinner } from '@qr-front/ui/components/Spinner'
import { cn, getVersionForClone } from '@qr-front/ui/utils'
import { useTranslations } from 'next-intl'
import { ReactNode } from 'react'

import { ROUTES } from '@/constants'
import { Link } from '@/i18n-routing'
import { THideSocials } from '@/utils'

import { OnboardingQrPreview } from '../../components/OnboardingScreen/OnboardingQrPreview'
import { ExistingAccountNotice } from './components/ExistingAccountNotice'
import { InputFieldEmailForm } from './components/InputFieldEmailForm'
import { InputFieldPasswordForm } from './components/InputFieldPasswordForm'
import {
	APPLE_AUTH_TYPE,
	AppleAuthButton,
} from './components/social-auth/AppleAuthButton'
import { FacebookAuthButton } from './components/social-auth/FacebookAuthButton'
import { GoogleAuthButton } from './components/social-auth/GoogleAuthButton'
import { SignUpPageDisclaimer } from './SignUpPageDisclaimer'
import {
	DEFAULT_HIDE_SOCIALS,
	hasHiddenSocial,
	hasVisibleSocialAuth,
} from './utils/hideSocials'

export function SignUpPageWelcome({
	responseGoogle,
	responseFacebook,
	handleFacebookEmailDeclined,
	handleAppleSubmit,
	handleAppleError,
	onSubmit,
	isLoading,
	hideSocials = DEFAULT_HIDE_SOCIALS,
	hidePassword = false,
	isPopupMode = false,
	showPasswordField = false,
	onPasswordStepBack,
	existingAccountStep,
}: {
	responseGoogle: (accessToken: string) => void
	responseFacebook: ({ accessToken }: { accessToken: string }) => void
	handleFacebookEmailDeclined?: () => void
	handleAppleSubmit: (token: string, signup?: boolean) => void
	handleAppleError?: (error: string) => void
	onSubmit: (
		event:
			| React.MouseEvent<HTMLButtonElement, MouseEvent>
			| React.KeyboardEvent<Element>,
	) => void
	isLoading: boolean
	hideSocials?: THideSocials
	hidePassword?: boolean
	isPopupMode?: boolean
	showPasswordField?: boolean
	onPasswordStepBack?: () => void
	existingAccountStep?: ReactNode
}) {
	const t = useTranslations()
	const isExistingAccountPasswordStep = hidePassword && showPasswordField

	const handleKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === 'Enter') {
			e.preventDefault()
			onSubmit(e)
		}
	}
	const showSocialAuth = hasVisibleSocialAuth(hideSocials)
	const socialLabelClassName = getVersionForClone({
		base: 'hidden tablet:inline',
		clone1: undefined,
	})
	// Vertically align if less then 3 socials auths are enabled
	const hasHiddenSocialValue = hasHiddenSocial(hideSocials)
	const separatorTitle = getVersionForClone({
		base: {
			className: 'hidden desktop:block',
			continueClassName: 'desktop:hidden',
		},
		clone1: {
			className: 'block',
			continueClassName: 'hidden',
		},
	})
	const featuresListKey = isPopupMode
		? 'auth__signup_welcome__secondary__popup_feature'
		: 'auth__signup_welcome__secondary__feature'

	return (
		<div
			className={cn(
				'flex w-full flex-row bg-white',
				isPopupMode
					? getVersionForClone({
							base: 'max-h-full max-w-none flex-col overflow-y-auto rounded-10 shadow-generator desktop:h-[min(657px,100%)] desktop:flex-row desktop:rounded-12',
							clone1: 'max-h-full max-w-[350px] flex-col overflow-y-auto rounded-10 shadow-generator desktop:h-[min(657px,100%)] desktop:max-w-none desktop:flex-row desktop:rounded-12',
						})
					: 'my-[56px] max-w-[350px] rounded-12 shadow-generator desktop:my-12 desktop:max-w-[976px]',
			)}
		>
			<div
				className={cn(
					'flex w-full flex-col items-center gap-8 text-center desktop:items-start desktop:text-start',
					isPopupMode
						? getVersionForClone({
								base: 'gap-8 px-4 py-6 desktop:w-1/2 desktop:justify-center desktop:rounded-l-12 desktop:p-6',
								clone1: 'gap-6 p-6 desktop:w-1/2 desktop:justify-center desktop:rounded-l-12 desktop:p-6',
							})
						: 'px-4 py-6 desktop:w-1/2 desktop:p-8',
					hasHiddenSocialValue &&
						!isPopupMode &&
						'desktop:justify-center',
				)}
			>
				{existingAccountStep}
				<form
					className={cn(
						'flex w-full flex-col',
						isPopupMode
							? getVersionForClone({
									base: 'gap-6',
									clone1: 'gap-6',
								})
							: 'gap-8',
						existingAccountStep && 'hidden',
					)}
					onKeyDown={handleKeyDown}
				>
					{isExistingAccountPasswordStep && onPasswordStepBack && (
						<button
							type="button"
							className="type-body-medium flex items-center gap-2 self-start text-dark-grey disabled:cursor-not-allowed disabled:opacity-50"
							onClick={onPasswordStepBack}
							disabled={isLoading}
						>
							<LineArrowLeft className="size-4" />
							{t('auth__common__back')}
						</button>
					)}
					{/*Header*/}
					<div className="flex flex-col gap-2">
						<div className="type-heading-3 text-black">
							{t('auth__signup_welcome__title')}
						</div>
						<div className="type-body-medium text-dark-grey">
							{t('auth__signup_welcome__description')}
						</div>
					</div>
					{isExistingAccountPasswordStep && (
						<ExistingAccountNotice
							message={t(
								'auth__signup_existing_account__password_info',
							)}
						/>
					)}
					<div className="flex flex-col gap-6">
						<div className="flex flex-col gap-4">
							{/* Input Login */}
							<InputFieldEmailForm
								readOnly={isExistingAccountPasswordStep}
								autoComplete="username"
							/>
							{/* Input Password */}
							{(!hidePassword || showPasswordField) && (
								<InputFieldPasswordForm
									skipValidation={hidePassword}
									autoComplete={
										isExistingAccountPasswordStep
											? 'current-password'
											: 'new-password'
									}
								/>
							)}
						</div>
						{/* Sign up button */}
						<Button
							visual="default"
							size="large"
							type="button"
							onClick={onSubmit}
							className="w-full"
							disabled={isLoading}
						>
							{t('ui__generic__continue')}
							{isLoading && <Spinner color="white" />}
						</Button>
					</div>
					{showSocialAuth && (
						<>
							{/* Divider */}
							<div className="relative w-full">
								<div className="absolute inset-0 flex items-center">
									<div className="w-full border-t border-border-grey-50"></div>
								</div>
								<div className="relative flex justify-center">
									<span
										className={cn(
											'bg-white px-4 text-grey',
											isPopupMode &&
												getVersionForClone({
													base: 'text-sm',
													clone1: 'type-body-medium',
												}),
											!isPopupMode && 'text-sm',
											separatorTitle.className,
										)}
									>
										{t('auth__common__separator_title')}
									</span>
									<span
										className={cn(
											'bg-white px-4 text-grey',
											isPopupMode &&
												getVersionForClone({
													base: 'text-sm',
													clone1: 'type-body-medium',
												}),
											!isPopupMode && 'text-sm',
											separatorTitle.continueClassName,
										)}
									>
										{t(
											'auth__common__continue_separator_title',
										)}
									</span>
								</div>
							</div>
							{/* Social auth */}
							<div
								className={cn(
									'flex flex-col items-center gap-4',
									isPopupMode &&
										getVersionForClone({
											base: 'flex-row tablet:flex-col',
											clone1: '',
										}),
								)}
							>
								{!hideSocials.GOOGLE && (
									<GoogleAuthButton
										onSubmit={responseGoogle}
										variant="text"
										labelClassName={socialLabelClassName}
									/>
								)}

								{!hideSocials.FACEBOOK && (
									<FacebookAuthButton
										onSubmit={responseFacebook}
										onEmailDeclined={
											handleFacebookEmailDeclined
										}
										variant="text"
										labelClassName={socialLabelClassName}
									/>
								)}

								{!hideSocials.APPLE && (
									<AppleAuthButton
										onSubmit={handleAppleSubmit}
										onError={handleAppleError}
										authType={APPLE_AUTH_TYPE.Signup}
										variant="text"
										labelClassName={socialLabelClassName}
									/>
								)}
							</div>
						</>
					)}
					{/* Forgot Password */}
					{!isPopupMode && (
						<div className="flex h-[22px] items-center justify-center gap-4 text-center desktop:justify-start">
							<span className="type-body-small text-dark-grey">
								{t('auth__signup__cta')}
							</span>
							<Link
								href={`${ROUTES.auth.login}?onboarding-flow=true`}
								className="type-button-small text-blue"
							>
								{t('auth__signup__cta_action')}
							</Link>
						</div>
					)}
					{/* Sign up disclaimer */}
					<SignUpPageDisclaimer isPopupMode={isPopupMode} />
				</form>
			</div>

			<div
				className={cn(
					'flex-1 desktop:flex desktop:items-center',
					isPopupMode
						? 'hidden w-full flex-col px-8 pb-8 desktop:flex desktop:w-1/2 desktop:rounded-r-12 desktop:py-6 desktop:pl-0 desktop:pr-4'
						: 'hidden w-1/2 py-8 pr-8',
				)}
			>
				<div
					className={cn(
						'flex w-full flex-col items-center gap-8 rounded-10 py-8',
						getVersionForClone({
							base: 'bg-light-grey-70',
							clone1: 'bg-light-purple',
						}),
						isPopupMode &&
							getVersionForClone({
								base: 'gap-4 px-14 py-6',
								clone1: 'gap-4 px-14 py-6',
							}),
					)}
				>
					{/*Header*/}
					<div className="type-heading-3 text-center text-black">
						{t('auth__signup_welcome__secondary__title')}
					</div>
					<div
						className={cn(
							'flex w-full justify-center',
							isPopupMode &&
								'border-b border-border-grey-50 pb-6',
						)}
					>
						<OnboardingQrPreview
							frameLessSize={196}
							size={isPopupMode ? 137 : 120}
						/>
					</div>
					{!isPopupMode && (
						<div className="-mt-px w-full px-14">
							<div className="w-full border-t border-border-grey-50"></div>
						</div>
					)}
					{/*Feature List*/}
					<div
						className={cn(
							'flex w-full flex-col gap-4 text-body-small text-dark-grey',
							isPopupMode ? 'gap-2' : 'px-14',
						)}
					>
						{t.rich(featuresListKey, {
							li: (chunks) => (
								<div
									key={chunks?.toString()}
									className={cn(
										'flex flex-row gap-2',
										isPopupMode
											? 'items-start'
											: 'items-center',
									)}
								>
									<SolidCheck
										className={cn(
											'h-4 w-4 shrink-0',
											isPopupMode && 'mt-0.5',
										)}
									/>
									{chunks}
								</div>
							),
						})}
					</div>
				</div>
			</div>
		</div>
	)
}
